import React, { useEffect, useRef, useState } from 'react';

import { Modal } from 'flowbite';

import ApproverRulesBlockedList from '../organisations/users/ApproverRulesBlockedList';
import { DIALOG_BUTTON, PER_PAGE_OPTIONS } from '../organisations/users/organisationUserFormat';

import ProjectUserModal from './users/manage/ProjectUserModal';
import ProjectUserRow, { CELL, ROW, TABLE } from './users/manage/ProjectUserRow';
import ProjectUsersToolbar from './users/manage/ProjectUsersToolbar';
import useProjectUserList from './users/manage/useProjectUserList';

import { modalOptions } from 'Helpers/modalOptions';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import PerPageSelect from 'CommonComponents/PerPageSelect';
import TablePagination from 'CommonComponents/TablePagination';

import { useToast } from 'Hooks/useToast';

// The wizard's panel is narrower than the table: emails may break there, so the
// Actions column stays in view without a sideways scroll.
const EMBEDDED_TABLE = '[&_td:nth-child(2)]:![overflow-wrap:anywhere]';

const COLUMNS = () => [$t('Name'), $t('Email'), $t('Role'), $t('Departments'), $t('Venues'), $t('Modules'), $t('Last login'), $t('Actions')];

/**
 * The project's users: search and filters, the condensed table, and the
 * Add / Edit pop-up. `embedded` drops the card and heading for the set-up
 * wizard's panel.
 */
function UsersTable(props) {
    const embedded = !!props.embedded;
    const list = useProjectUserList({ listUrl: props.listUsersUrl });
    const { query, setQuery, meta, rows, refresh } = list;
    const toast = useToast();

    // The pop-up: null closed, 'add', or the row being edited.
    const [editing, setEditing] = useState(null);

    const [deletedUser, setDeletedUser] = useState(null);
    // Why the last delete was refused: approval rules naming the user, or a warning that they are the only Event Admin.
    const [deleteBlock, setDeleteBlock] = useState(null);
    const [usersDeleteModalElement, setUsersDeleteModalElement] = useState(null);
    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);
    const usersDeleteModalRef = useRef();

    useEffect(() => {
        if (props.canDelete) {
            usersDeleteModalRef && setUsersDeleteModalElement(new Modal(usersDeleteModalRef.current, modalOptions(false)));
        }
    }, []);

    useEffect(() => {
        if (deletedUser) {
            usersDeleteModalElement && usersDeleteModalElement.show();
        }
    }, [deletedUser]);

    const clearDeletedUser = () => {
        setDeletedUser(null);
        setDeleteBlock(null);
        usersDeleteModalElement && usersDeleteModalElement.hide();
    };

    const cleanConfirmationModalStates = () => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    };

    const refreshAll = () => {
        refresh();
        props.fetchOrganisationUsers();
    };

    const triggerDeleteUser = () => {
        if (!props.canDelete) {
            return;
        }
        setActionInProgress(true);
        axios
            .delete(props.deleteUserUrl.replace('user_id', deletedUser.id), {
                data: { confirm_last_admin: deleteBlock?.error_code === 'LAST_EVENT_ADMIN' },
            })
            .then(() => {
                toast.success($t('User was deleted successfully'));
                refreshAll();
                setActionCompleted(true);
                clearDeletedUser();
            })
            .catch((e) => {
                setActionInProgress(false);
                const response = e?.response?.data;

                if (response?.error_code === 'APPROVER_RULES' || response?.error_code === 'LAST_EVENT_ADMIN') {
                    setDeleteBlock(response);

                    return;
                }

                toast.error(response?.message || response?.error || $t('Something went wrong'));
            });
    };

    const onSaved = (added) => {
        setEditing(null);
        toast.success(added ? $t('The user was added') : $t('The user was updated'));
        refreshAll();
    };

    const columns = COLUMNS();

    return (
        <>
            <div className={embedded ? 'tw-space-y-4' : 'table__card tw-space-y-4'}>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4">
                    {!embedded && <h4 className="table__header tw-mb-0">{$t('Users')}</h4>}
                    {!!props.canCreate && (
                        <Button variant="primary" className={`tw-ms-auto ${DIALOG_BUTTON}`} onClick={() => setEditing('add')}>
                            {$t('Add user to project')}
                        </Button>
                    )}
                </div>

                <ProjectUsersToolbar
                    query={query}
                    searchInput={list.searchInput}
                    roles={props.roles}
                    departments={props.departments}
                    venues={props.venues}
                    modules={props.modules}
                    onSearchChange={list.setSearchInput}
                    onChange={setQuery}
                />

                {list.error && <Alert type="error">{list.error}</Alert>}

                {list.loading && !meta ? (
                    <p className="tw-mb-0 tw-text-sm tw-text-charcoal-75">{$t('Loading')}…</p>
                ) : (
                    <div className="inbud-table_wrapper">
                        <table className={embedded ? `${TABLE} ${EMBEDDED_TABLE}` : TABLE}>
                            <thead>
                                <tr>
                                    {columns.map((column) => (
                                        <th key={column} className="!tw-text-start">
                                            {column}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 && (
                                    <tr className={ROW}>
                                        <td colSpan={columns.length} className={`${CELL} !tw-text-center tw-text-charcoal-75`}>
                                            {$t('No users match these filters.')}
                                        </td>
                                    </tr>
                                )}
                                {rows.map((user) => (
                                    <ProjectUserRow
                                        key={user.id}
                                        user={user}
                                        canEdit={!!props.canEdit}
                                        canDelete={!!props.canDelete}
                                        onEdit={setEditing}
                                        onRemove={setDeletedUser}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {meta && (
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                        <div className="tw-flex tw-items-center tw-gap-3">
                            <PaginationInfo from={meta.from} to={meta.to} total={meta.total} />
                            {meta.last_page > 1 && (
                                <TablePagination
                                    lastPage={meta.last_page}
                                    currentPage={meta.current_page}
                                    pageChange={({ selected }) => list.setPage(selected + 1)}
                                />
                            )}
                        </div>
                        <PerPageSelect
                            value={query.per_page}
                            options={PER_PAGE_OPTIONS}
                            label={$t('Rows per page')}
                            onChange={(event) => setQuery({ per_page: Number(event.target.value) })}
                        />
                    </div>
                )}
            </div>

            {(!!props.canCreate || !!props.canEdit) && (
                <ProjectUserModal
                    open={editing !== null}
                    user={editing === 'add' ? null : editing}
                    roles={props.roles}
                    departments={props.departments}
                    costcenters={props.costcenters}
                    revDepartments={props.revDepartments}
                    revCostcenters={props.revCostcenters}
                    venues={props.venues}
                    modules={props.modules}
                    organisationUsers={props.organisationUsers}
                    storeUrl={props.storeUserUrl}
                    updateUrl={props.updateUserUrl}
                    canAssignEventAdmin={!!props.canAssignEventAdmin}
                    canEnable={!!props.canEnable}
                    authId={props.authId}
                    onClose={() => setEditing(null)}
                    onSaved={onSaved}
                />
            )}

            {!!props.canDelete && (
                <ConfirmationModal
                    closeHandler={clearDeletedUser}
                    triggerAction={triggerDeleteUser}
                    cleanStates={cleanConfirmationModalStates}
                    allowAction={deletedUser != undefined}
                    modalId={'delete-user'}
                    modalRef={usersDeleteModalRef}
                    modalElement={usersDeleteModalElement}
                    request={actionInProgress}
                    actionCompleted={actionCompleted}
                    actionFailed={actionFailed}
                    confirmationMsg={$t('Are you sure you want to delete {{name}}?', { name: deletedUser?.name })}
                    actionCompletedMsg=""
                    actionFailedMsg=""
                    title={$t('Delete User')}
                    cancelButtonText={$t('Close')}
                    primaryButtonText={$t('Delete')}
                    primaryButtonDisabled={deleteBlock?.error_code === 'APPROVER_RULES'}
                >
                    {deleteBlock?.error_code === 'APPROVER_RULES' && (
                        <div className="tw-mb-4">
                            <ApproverRulesBlockedList rules={deleteBlock.rules} linkUrl={deleteBlock.link_url} />
                        </div>
                    )}
                    {deleteBlock?.error_code === 'LAST_EVENT_ADMIN' && (
                        <p className="tw-mb-4 tw-text-0.5sm tw-font-medium tw-text-red">
                            {$t('{{name}} is the only Event Admin of this project. Select Delete again to remove them anyway.', {
                                name: deletedUser?.name,
                            })}
                        </p>
                    )}
                </ConfirmationModal>
            )}
        </>
    );
}

export default UsersTable;
